// src/api/realtime.js
import { io } from "socket.io-client";
import api, { getAuthToken } from "./axios";
export {
  makeStatusDayRoom,
  makeCimInterdicoesRoom,
  makeEstaleiroDepositoRoom,
  makeEstaleiroLoteRoom,
  makeWorkflowAllRoom,
  makeWorkflowDayRoom,
  makeWorkflowItemRoom,
} from "./realtime.rooms";

export const RT = {
  // eventos já existentes no seu projeto
  STATUS_DIARIO_INVALIDATE: "status_diario:invalidate",
  STATUS_DIARIO_TIMER_UPDATE: "status_diario:timer_update",
  WF3_CHANGED: "wf3:changed",

  // ✅ WF2 (para você emitir do backend quando quiser)
  WF2_CHANGED: "wf2:changed",
  ESTALEIRO_DEPOSITO_CHANGED: "estaleiro:deposito_changed",
  CIM_INTERDICOES_CHANGED: "cim:interdicoescim:changed",

  // ✅ eventos “genéricos” que seu front já usa (W2Card / wf2/api)
  CREATED: "wf2:created",
  UPDATED: "wf2:updated",
  MOVED: "wf2:moved",
  DELETED: "wf2:deleted",
};

const RT_DEBUG =
  (process.env.REACT_APP_RT_DEBUG || "0") === "1" ||
  (typeof window !== "undefined" && window.localStorage?.getItem("rt.debug") === "1");

function log(...a) {
  if (RT_DEBUG) console.debug("[RT]", ...a);
}

export const RT_META = {
  CONNECTED: "connected",
  DISCONNECTED: "disconnected",
  RECONNECTED: "reconnected",
  JOINED: "joined",
  ERROR: "error",
};

// ✅ exportado para compatibilidade (alguns lugares importam { socket })
export let socket = null;

let connectPromise = null;
let hasConnectedOnce = false;

// rooms que o client QUER estar (persistente, rejoin no reconnect)
const desiredRooms = new Set();
const joinedRooms = new Set();

// prefixo -> room (garante 1 room por escopo)
const scoped = new Map();

const metaHandlers = new Map();

function readEnv(name) {
  try {
    if (typeof import.meta !== "undefined" && import.meta.env?.[name] != null) {
      return import.meta.env[name];
    }
  } catch {}
  try {
    if (typeof process !== "undefined" && process.env?.[name] != null) {
      return process.env[name];
    }
  } catch {}
  return undefined;
}

function emitMeta(eventName, payload = {}) {
  const handlers = metaHandlers.get(eventName);
  if (!handlers?.size) return;
  for (const handler of Array.from(handlers)) {
    try {
      handler(payload);
    } catch (err) {
      console.error("[RT] meta handler error:", err?.message || err);
    }
  }
}

function apiBaseToOrigin(apiBase) {
  const s = String(apiBase || "").trim();
  const browserOrigin =
    typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : "";

  if (!s || s === "/api" || s === "api") return browserOrigin;
  if (/^https?:\/\//i.test(s)) return s.replace(/\/api\/?$/, "");
  if (s.startsWith("/")) return browserOrigin;
  return s.replace(/\/api\/?$/, "") || browserOrigin;
}

function ensureSocket() {
  if (socket) return socket;

  const apiBase =
    api?.defaults?.baseURL ||
    readEnv("VITE_API_URL") ||
    readEnv("REACT_APP_API_BASE") ||
    readEnv("REACT_APP_API_URL") ||
    "/api";
  const origin = apiBaseToOrigin(apiBase);
  const path = readEnv("REACT_APP_SOCKETIO_PATH") || readEnv("REACT_APP_SOCKET_PATH") || "/ws";

  socket = io(origin, {
    path,
    transports: ["websocket", "polling"],
    withCredentials: true,
    autoConnect: false,
  });

  log("socket config", { origin, path, apiBase });

  socket.on("connect", () => {
    log("connected", socket.id);
    joinedRooms.clear();
    syncDesiredRooms();
    emitMeta(hasConnectedOnce ? RT_META.RECONNECTED : RT_META.CONNECTED, {
      socketId: socket.id,
      desiredRooms: Array.from(desiredRooms),
      ts: Date.now(),
    });
    hasConnectedOnce = true;
  });

  socket.on("disconnect", (reason) => {
    log("disconnect", reason);
    joinedRooms.clear();
    emitMeta(RT_META.DISCONNECTED, { reason, ts: Date.now() });
  });
  socket.on("connect_error", (e) => {
    log("connect_error", e?.message || e);
    emitMeta(RT_META.ERROR, { op: "connect", error: e, ts: Date.now() });
  });
  socket.io?.on?.("reconnect", (n) => log("reconnect", n));
  socket.on("rt:joined", (payload) => {
    const rooms = Array.isArray(payload?.rooms) ? payload.rooms : [];
    const denied = Array.isArray(payload?.denied) ? payload.denied : [];
    for (const room of rooms) joinedRooms.add(room);
    for (const deniedRoom of denied) joinedRooms.delete(deniedRoom?.room);
    log("rt:joined", payload);
    emitMeta(RT_META.JOINED, {
      rooms,
      denied,
      joinedRooms: Array.from(joinedRooms),
      desiredRooms: Array.from(desiredRooms),
      ts: Date.now(),
    });
  });
  socket.on("rt:error", (payload) => {
    log("rt:error", payload);
    emitMeta(RT_META.ERROR, { ...payload, op: payload?.op || "rt", ts: Date.now() });
  });

  return socket;
}

function syncDesiredRooms() {
  if (!socket || !socket.connected || desiredRooms.size === 0) return;
  socket.emit("rt:join", { rooms: Array.from(desiredRooms) });
  log("join ->", Array.from(desiredRooms));
}

function emitJoin(room) {
  if (!socket || !socket.connected || !room) return;

  // ✅ formato único que o backend espera
  socket.emit("rt:join", { rooms: [room] });

  log("join ->", room);
}

function emitLeave(room) {
  if (!socket || !socket.connected || !room) return;

  // ✅ formato único que o backend espera
  socket.emit("rt:leave", { rooms: [room] });

  log("leave ->", room);
}

export function getSocket() {
  return socket;
}

export async function connectRealtime() {
  const s = ensureSocket();
  if (s.connected) return s;

  if (connectPromise) return connectPromise;

  connectPromise = new Promise((resolve, reject) => {
    const onOk = () => {
      cleanup();
      connectPromise = null;
      resolve(s);
    };
    const onErr = (e) => {
      cleanup();
      connectPromise = null;
      reject(e);
    };
    const cleanup = () => {
      s.off("connect", onOk);
      s.off("connect_error", onErr);
    };

    s.once("connect", onOk);
    s.once("connect_error", onErr);

    Promise.resolve(getAuthToken())
      .catch(() => null)
      .then((token) => {
        s.auth = token ? { token } : {};
        log("connect() called");
        s.connect();
      });
  });

  return connectPromise;
}

// ✅ join/leave persistentes (rejoin no reconnect)
export function joinRoom(room) {
  if (!room) return;
  const alreadyDesired = desiredRooms.has(room);
  desiredRooms.add(room);
  ensureSocket();
  if (socket.connected) {
    if (alreadyDesired || joinedRooms.has(room)) return;
    emitJoin(room);
  }
  else log("join queued", room);
}

export function leaveRoom(room) {
  if (!room) return;
  desiredRooms.delete(room);
  joinedRooms.delete(room);
  ensureSocket();
  if (socket.connected) emitLeave(room);
  else log("leave queued", room);
}

// ✅ 1 room por prefixo (evita empilhar dias)
export function setScopedRoom(prefix, roomOrNull) {
  const next = roomOrNull || null;
  const prev = scoped.get(prefix) || null;
  if (prev === next) return;

  if (prev) leaveRoom(prev);
  scoped.set(prefix, next);
  if (next) joinRoom(next);

  log("scopedRoom", { prefix, prev, next });
}

// eventos
export function on(eventName, handler) {
  if (!eventName || typeof eventName !== "string") return;
  ensureSocket();
  socket.on(eventName, handler);
}

export function off(eventName, handler) {
  if (!socket) return;
  if (!eventName || typeof eventName !== "string") return;
  socket.off(eventName, handler);
}

export function onRealtimeMeta(eventName, handler) {
  if (!eventName || typeof handler !== "function") return () => {};
  if (!metaHandlers.has(eventName)) metaHandlers.set(eventName, new Set());
  metaHandlers.get(eventName).add(handler);
  return () => offRealtimeMeta(eventName, handler);
}

export function offRealtimeMeta(eventName, handler) {
  const handlers = metaHandlers.get(eventName);
  if (!handlers) return;
  handlers.delete(handler);
  if (handlers.size === 0) metaHandlers.delete(eventName);
}

export function getDesiredRooms() {
  return Array.from(desiredRooms);
}

export function getJoinedRooms() {
  return Array.from(joinedRooms);
}
